import React, { Suspense, useState } from 'react';
import useIsOwner from '../../hooks/useIsOwner';

const PostToXModal = React.lazy(() => import('../PostToXModal'));

// What each backend `kind` means to a reader. "Filler" never reaches here
// unless the user asked to see it.
export const KIND_LABEL = {
  major: 'Major outlet',
  release: 'Company release',
  news: 'News',
  opinion: 'Opinion',
  filler: 'Filler',
};

export const isHeadline = (a) => a.kind === 'major' || a.kind === 'release' || a.kind === 'news';

export function relTime(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 60) return `${mins || 1}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Free, instant, available to everyone: the article's own headline and link in
// X's compose window. No LLM, nothing posted on anyone's behalf.
function shareIntent(article, symbol) {
  const text = `${article.title} $${symbol}`;
  return `https://x.com/intent/post?text=${encodeURIComponent(text)}&url=${encodeURIComponent(article.url)}`;
}

/**
 * "Share on X" for everyone; "Draft in my voice" for the owner, which runs the
 * article through the founder voice stack (the post LEADS with the news, the
 * grade is the closing line) and opens the same review modal Alpha answers use.
 */
export function ShareActions({ article, symbol, compact = false }) {
  const isOwner = useIsOwner();
  const [drafting, setDrafting] = useState(false);
  const sym = symbol || (article.symbols && article.symbols[0]) || article.symbol;

  return (
    <div className={`nf-share ${compact ? 'compact' : ''}`} onClick={(e) => e.stopPropagation()}>
      <a
        className="nf-share-btn"
        href={shareIntent(article, sym)}
        target="_blank"
        rel="noopener noreferrer"
        title="Share this article on X"
      >
        𝕏 Share
      </a>
      {isOwner && (
        <button
          type="button"
          className="nf-share-btn voice"
          onClick={() => setDrafting(true)}
          title="Draft a post in your voice that leads with this article"
        >
          ✍ Draft in my voice
        </button>
      )}
      {drafting && (
        <Suspense fallback={null}>
          <PostToXModal
            title={`Post this ${sym} article to X`}
            draftRequest={{
              endpoint: '/news/draft-post',
              body: {
                url: article.url,
                title: article.title,
                source: article.source,
                summary: article.summary,
                symbol: sym,
              },
            }}
            onClose={() => setDrafting(false)}
          />
        </Suspense>
      )}
    </div>
  );
}
